import { observer } from "mobx-react-lite";
import { Popup } from "react-map-gl/mapbox";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import type { MapStore } from "@/components/Map/stores/MapStore";

const POPUP_ANIM = {
	initial: { opacity: 0, y: 6, scale: 0.98 },
	animate: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.1, ease: "easeOut" } },
	exit: { opacity: 0, y: 6, scale: 0.98, transition: { duration: 0.1, ease: "easeIn" } },
};

const formatNumber = (value: number | null | undefined, decimals: number): string | null => {
	if (value == null || !Number.isFinite(value)) return null;
	return value.toFixed(decimals);
};

type StatProps = {
	label: string;
	value: string | null | undefined;
	unit?: string;
	className?: string;
};

const Stat = ({ label, value, unit, className }: StatProps) => (
	<div className={`flex flex-col ${className ?? ""}`}>
		<span className="text-[10px] uppercase tracking-wide text-muted-foreground leading-tight">{label}</span>
		{value == null ? (
			<span className="text-xs italic text-muted-foreground leading-tight mt-0.5">Unknown</span>
		) : (
			<span className="text-xs font-semibold tabular-nums leading-tight mt-0.5">
				{value}
				{unit ? <span className="font-normal text-muted-foreground ml-0.5">{unit}</span> : null}
			</span>
		)}
	</div>
);

interface ConnectionsHoverPopupProps {
	mapStore: MapStore<any>;
}

export const ConnectionsHoverPopup = observer(({ mapStore }: ConnectionsHoverPopupProps) => {
	const popup = mapStore.popupsStore.hoverPopup;
	const connection =
		popup?.kind === "connection"
			? mapStore.connectionsStore.connectionsApi.data?.items?.find(item => item.id === popup.id)
			: null;

	return (
		<AnimatePresence initial={false}>
			{popup?.kind === "connection" && connection && (
				<Popup
					key={connection.id}
					className="popup-overlay-tag"
					longitude={popup.lng}
					latitude={popup.lat}
					closeButton={false}
					closeOnClick={false}
					closeOnMove={false}
					anchor="bottom"
					offset={14}
					maxWidth="280px"
				>
					<motion.div
						className="relative rounded-lg bg-white shadow-lg border border-gray-200 px-3 py-2.5 min-w-[220px] pointer-events-none"
						variants={POPUP_ANIM}
						initial="initial"
						animate="animate"
						exit="exit"
					>
						<div className="flex items-center gap-1.5 min-w-0 pb-2 border-b border-gray-100">
							<span className="text-sm font-semibold text-foreground truncate">
								{connection.fromStructureName ?? `#${connection.fromStructureId}`}
							</span>
							<ArrowRight className="w-3.5 h-3.5 shrink-0 text-[#003BFF]" />
							<span className="text-sm font-semibold text-foreground truncate">
								{connection.toStructureName ?? `#${connection.toStructureId}`}
							</span>
							<span className="ml-auto inline-flex items-center rounded-full bg-sky-50 text-sky-700 px-1.5 h-4 text-[10px] font-medium shrink-0">
								{connection.connectionType}
							</span>
						</div>

						<div className="grid grid-cols-2 gap-x-3 gap-y-2 pt-2">
							<Stat label="Diameter" value={formatNumber(connection.diameterMm, 0)} unit="mm" />
							<Stat label="Length" value={formatNumber(connection.lengthM, 1)} unit="m" />
							<Stat label="Slope" value={formatNumber(connection.slopeMPerM, 4)} unit="m/m" />
							<Stat label="Manning" value={formatNumber(connection.manningN, 3)} />
							<Stat label="Material" value={connection.materialName} className="col-span-2" />
						</div>

						{connection.notes && (
							<p className="text-xs text-muted-foreground leading-relaxed pt-2 mt-2 border-t border-gray-100">
								{connection.notes}
							</p>
						)}
					</motion.div>
				</Popup>
			)}
		</AnimatePresence>
	);
});
